import { notifyManager } from "@tanstack/react-query";
import type { ModelName, MutationMethodForModel } from "../../types";
import { invalidateTellescopeModelQueries, setTellescopeModelQueriesData } from "../../utils";
import { getAllMutationSideEffects } from "./get-all-mutation-side-effects";
import { getMutationSideEffectFilters } from "./get-mutation-side-effect-filters";
import { getMutationSideEffectUpdaters } from "./get-mutation-side-effect-updaters";
import { getMutationSideEffectsWithUpdaters } from "./get-mutation-side-effects-with-updaters";
import { splitMutationSideEffectUpdaterTypes } from "./split-mutation-side-effect-updater-types";
import type { AutomaticMutationSideEffects } from "./types";

export interface GetAutomatedMutationSideEffectArgs<
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
> {
  model: Model;
  method: Method;
}

export type GetAutomatedMutationSideEffectReturn<
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
  TError = Error,
> = AutomaticMutationSideEffects<Model, Method, TError> | undefined;

/**
 * Generates automatic mutation side effects for TanStack Query mutations.
 *
 * This function creates optimized cache update strategies for Tellescope mutations,
 * providing both optimistic updates (`onMutate`) and post-mutation cache synchronization
 * (`onSuccess`). It supports two cache update strategies:
 *
 * - **`"update"` strategy**: Directly updates query cache data with the mutation response,
 *   avoiding unnecessary refetches. Best for mutations where the response contains complete
 *   updated data. Will fall back to invalidation if filters are configured with no updater.
 *
 * - **`"invalidate"` strategy**: Invalidates affected queries, triggering automatic refetches.
 *   Best for mutations where the response may not contain all affected data or when
 *   dependencies between entities require fresh data.
 *
 * @template Model - The Tellescope model name (e.g., "users", "appointments")
 * @template Method - The mutation method for the model (e.g., "create_user", "update_appointment")
 * @template TError - The error type for mutation failures (defaults to Error)
 *
 * @param args - Configuration object
 * @param args.model - The Tellescope model name
 * @param args.method - The mutation method to generate side effects for
 *
 * @returns An object containing `onMutate` and `onSuccess` callbacks, or `undefined`
 *          if no automatic side effects are configured for this model/method combination.
 *
 * @see {@link AutomaticMutationSideEffects} for the return type structure
 * @see {@link getMutationSideEffectsWithUpdaters} for the updater configuration
 * @see {@link getMutationSideEffectFilters} for the invalidation filter configuration
 */
export const getAutomatedMutationSideEffects = <
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
}: GetAutomatedMutationSideEffectArgs<Model, Method>):
  | GetAutomatedMutationSideEffectReturn<Model, Method, TError>
  | undefined => {
  const result: AutomaticMutationSideEffects<Model, Method, TError> = {
    // @ts-expect-error return type is broader than expected, but is correct
    onMutate: (queryClient, sideEffectContext) => (request) => {
      const { methodConfigs, otherModelConfigs } = getMutationSideEffectsWithUpdaters<Model, Method>({
        model,
        method,
        request,
        context: sideEffectContext,
      });

      // Batch all cache writes (own model + other models) into a single
      // notification pass to avoid cascading re-renders during optimistic updates.
      return notifyManager.batch(() => {
        const context = setTellescopeModelQueriesData<Model, TError>({
          queryClient,
          model,
          methodConfigs,
        });

        if (otherModelConfigs) {
          Object.entries(otherModelConfigs).forEach(([model, methodConfigs]) =>
            setTellescopeModelQueriesData<Exclude<ModelName, Model>>({
              queryClient,
              // @ts-expect-error type is too broad
              model,
              // @ts-expect-error type is too broad
              methodConfigs,
            })
          );
        }

        return context;
      });
    },
    onSuccess: (queryClient, strategy, sideEffectContext) => (response, request) => {
      switch (strategy) {
        case "invalidate": {
          const methodFilters = getMutationSideEffectFilters<Model, Method>({
            model,
            method,
            context: sideEffectContext,
          })?.(request, response);

          invalidateTellescopeModelQueries({
            queryClient,
            model,
            methodFilters,
          });

          // Handle _otherModels - always invalidate (ignore any updaters)
          const fullConfig = getMutationSideEffectUpdaters<Model, Method>({
            model,
            method,
          })?.(request, response, sideEffectContext);

          if (fullConfig?._otherModels) {
            Object.entries(fullConfig._otherModels).forEach(([otherModel, methodConfigs]) => {
              invalidateTellescopeModelQueries<Exclude<ModelName, Model>>({
                queryClient,
                model: otherModel as Exclude<ModelName, Model>,
                // @ts-expect-error type is too broad
                methodFilters: methodConfigs,
              });
            });
          }

          return;
        }
        case "update": {
          const { withUpdaters, withoutUpdaters } = getAllMutationSideEffects<Model, Method>({
            model,
            method,
            request,
            response,
            context: sideEffectContext,
          });

          // Batch all cache writes and invalidations (own model + other models)
          // into a single notification pass to avoid cascading re-renders.
          notifyManager.batch(() => {
            if (Object.keys(withUpdaters).length) {
              setTellescopeModelQueriesData({
                queryClient,
                model,
                methodConfigs: withUpdaters,
              });
            }

            if (Object.keys(withoutUpdaters).length) {
              invalidateTellescopeModelQueries({
                queryClient,
                model,
                methodFilters: withoutUpdaters,
              });
            }

            // Handle _otherModels - mixed approach (update if possible, invalidate if not)
            const fullConfig = getMutationSideEffectUpdaters<Model, Method>({
              model,
              method,
            })?.(request, response, sideEffectContext);

            if (fullConfig?._otherModels) {
              Object.entries(fullConfig._otherModels).forEach(([otherModel, methodConfigs]) => {
                // Split by updaters using splitMutationSideEffectUpdaterTypes
                // @ts-expect-error methodConfigs type is too broad but runtime is safe
                const splitConfigs = splitMutationSideEffectUpdaterTypes(methodConfigs);

                // Update queries that have updaters
                if (Object.keys(splitConfigs.withUpdaters).length) {
                  setTellescopeModelQueriesData({
                    queryClient,
                    model: otherModel as Exclude<ModelName, Model>,
                    // @ts-expect-error type is too broad
                    methodConfigs: splitConfigs.withUpdaters,
                  });
                }

                // Invalidate queries that don't have updaters
                if (Object.keys(splitConfigs.withoutUpdaters).length) {
                  invalidateTellescopeModelQueries({
                    queryClient,
                    model: otherModel as Exclude<ModelName, Model>,
                    // @ts-expect-error type is too broad
                    methodFilters: splitConfigs.withoutUpdaters,
                  });
                }
              });
            }
          });

          return;
        }
      }
    },
  };

  return result;
};
